export type Site = 'pandalive' | 'bktv';

export type SiteInfo = {
  name: string;
  host: string;
};

const DESKTOP_SITE_SUBDOMAINS = new Set(['www', 'w2']);
const MOBILE_SITE_SUBDOMAINS = new Set(['m', 'm2']);

// ─────────────────────────────────────────────────────────────────────────────
// Non-canonical hostname prefixes
// ─────────────────────────────────────────────────────────────────────────────
//
// Environment indicators that sit in front of the canonical hostname:
//   - `local.bktv.kr`              local dev (host alias)
//   - `migrate.pandalive.co.kr`    AWS migration dev environment
//
// These are stripped during normalization so site detection and routing logic
// only ever see the canonical hostname. When a function needs to preserve the
// env context (e.g. `resolveMobileHostname` keeping `local.` → `local.m.`),
// it reads the prefix via `getNonCanonicalPrefix` and re-applies it.
//
// Adding a new environment prefix is a one-line change: append to the array.
// All downstream functions inherit the behavior automatically.

const NON_CANONICAL_PREFIXES = ['local.', 'migrate.'] as const;

const getNonCanonicalPrefix = (hostname: string): string => {
  for (const prefix of NON_CANONICAL_PREFIXES) {
    if (hostname.startsWith(prefix)) return prefix;
  }
  return '';
};

const stripNonCanonicalPrefix = (hostname: string): string => {
  const prefix = getNonCanonicalPrefix(hostname);
  return prefix ? hostname.slice(prefix.length) : hostname;
};

// ─────────────────────────────────────────────────────────────────────────────
// Site definitions
// ─────────────────────────────────────────────────────────────────────────────

export const siteInfo: Record<Site, SiteInfo> = {
  pandalive: { name: '팬더티비', host: 'pandalive.co.kr' },
  bktv: { name: 'BK티비', host: 'bktv.kr' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Core hostname utilities
// ─────────────────────────────────────────────────────────────────────────────

export const extractHostname = (input: string): string => {
  if (!input || typeof input !== 'string') return '';

  try {
    if (input.includes('://')) {
      return new URL(input).hostname.toLowerCase();
    }
  } catch {
    // Fall back to loose hostname parsing for host[:port] inputs.
  }

  return input
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/:\d+$/, '');
};

export const normalizeHostname = (rawHost: string): string => {
  return stripNonCanonicalPrefix(extractHostname(rawHost));
};

// ─────────────────────────────────────────────────────────────────────────────
// Site detection
// ─────────────────────────────────────────────────────────────────────────────

export const isPandaliveHostname = (rawHost: string): boolean => {
  return normalizeHostname(rawHost).includes('pandalive');
};

export const isBktvHostname = (rawHost: string): boolean => {
  return normalizeHostname(rawHost).includes('bktv');
};

export const resolveSiteFromHostname = (rawHost: string): Site => {
  if (isBktvHostname(rawHost)) {
    return 'bktv';
  }

  return 'pandalive';
};

export const resolveSiteInfoFromHostname = (rawHost: string): SiteInfo => {
  const normalized = normalizeHostname(rawHost);
  const resolvedSite = resolveSiteFromHostname(normalized);
  const resolvedSiteInfo = { ...siteInfo[resolvedSite] };
  const [subdomain] = normalized.split('.');

  if (DESKTOP_SITE_SUBDOMAINS.has(subdomain)) {
    resolvedSiteInfo.host = `www.${resolvedSiteInfo.host}`;
  } else if (MOBILE_SITE_SUBDOMAINS.has(subdomain)) {
    resolvedSiteInfo.host = `m.${resolvedSiteInfo.host}`;
  }

  return resolvedSiteInfo;
};

export const getCurrentSite = (): Site => {
  if (typeof window === 'undefined') return 'pandalive';
  return resolveSiteFromHostname(window.location.hostname);
};

export const getSiteInfo = (): SiteInfo => {
  if (typeof window === 'undefined') return siteInfo.pandalive;
  return resolveSiteInfoFromHostname(window.location.hostname);
};

// ─────────────────────────────────────────────────────────────────────────────
// Hostname classification
// ─────────────────────────────────────────────────────────────────────────────

export const resolvePresetHostname = (rawHost: string): string => {
  const normalized = normalizeHostname(rawHost);

  if (normalized.includes('bktv.dmm.co.jp')) {
    return 'bktv.dmm.co.jp';
  }

  if (normalized.includes('pandalive.co.kr')) {
    return 'pandalive.co.kr';
  }

  if (normalized.includes('dev.pandalive.co.jp')) {
    return 'dev.pandalive.co.jp';
  }

  if (normalized.includes('pandalive.co.jp')) {
    return 'pandalive.co.jp';
  }

  if (normalized.includes('bktv')) {
    return 'bktv.kr';
  }

  return normalized;
};

/**
 * Any hostname that isn't canonical production pandalive traffic:
 *   - has a non-canonical env prefix (local., migrate.)
 *   - contains 'dev' (dev.pandalive.co.jp, mdev.pandalive.co.jp, etc.)
 *   - is a bktv hostname (bktv is currently the dev/internal brand)
 */
export const isNonProductionHostname = (rawHost: string): boolean => {
  const extracted = extractHostname(rawHost);
  return Boolean(getNonCanonicalPrefix(extracted)) || extracted.includes('dev') || isBktvHostname(extracted);
};

export const isMobileSiteHostname = (rawHost: string): boolean => {
  const normalized = normalizeHostname(rawHost);
  const [subdomain] = normalized.split('.');
  return MOBILE_SITE_SUBDOMAINS.has(subdomain) || normalized.startsWith('mdev.');
};

export const resolveMobileHostname = (rawHost: string): string => {
  const extracted = extractHostname(rawHost);
  const normalized = stripNonCanonicalPrefix(extracted);
  const envPrefix = getNonCanonicalPrefix(extracted);

  if (normalized.includes('dev.pandalive.co.jp') || normalized.includes('mdev.pandalive.co.jp')) {
    return `${envPrefix}mdev.pandalive.co.jp`;
  }

  if (normalized.includes('bktv')) {
    return `${envPrefix}m.bktv.kr`;
  }

  return `${envPrefix}m.pandalive.co.kr`;
};

export const resolveMobileOriginFromHostname = (rawHost: string, protocol = 'https'): string => {
  return `${protocol}://${resolveMobileHostname(rawHost)}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// ASP (Application Service Provider) hostname classification
// ─────────────────────────────────────────────────────────────────────────────
//
// 우리 서비스가 ASP 로 노출되는 호스트는 다음 두 가지로만 분류된다.
//
//   1. iframe 타입 — 파트너 사이트가 자기 페이지에 직접 임베드하는 호스트.
//      부트스트랩: GET https://apiasp.pandalive.co.kr/v1/asp/auth?...&type=iframe
//      호스트:
//        - wasp.pandalive.co.kr        (PC 정식)
//        - masp.pandalive.co.kr        (모바일 정식)
//        - wasp-new.pandalive.co.kr    (신규 배포 슬롯, PC) ── 정식과 동일 취급
//        - masp-new.pandalive.co.kr    (신규 배포 슬롯, 모바일) ── 정식과 동일 취급
//      dev/local prefix (local.wasp., dev.masp. 등) 도 모두 포함.
//
//   2. popup (livetv) 타입 — 파트너 별 도메인을 새 창/탭 으로 띄우는 호스트.
//      부트스트랩: GET /v1/config/preset → aspCode 결정 → /v1/asp/auth?type=popup
//      호스트 패턴:
//        - livetv[숫자]?.{aspCode}.co.kr     (PC)
//        - mlivetv[숫자]?.{aspCode}.co.kr    (모바일)
//      dev/local prefix 포함.
//
// 두 변형은 모두 ASP 서비스이지만 부트스트랩 / 인증 / 비즈니스 로직 분기 가
// 완전히 다르다. 호스트가 늘어나거나 새 배포 슬롯이 추가되면 이 파일의
// 정규식 둘 만 갱신하면 된다 — aspConfigManager 와 미들웨어(PC/Mobile proxy) 는
// 여기서 export 하는 함수만 호출한다.

/** iframe 타입 ASP : wasp / masp 와 -new 배포 슬롯, dev/local prefix 변형 포함. */
const IFRAME_ASP_HOSTNAME_PATTERN = /^(?:local\.|dev\.)?(?:wasp|masp)(?:-new)?\.pandalive\.co\.kr$/;

/** popup(livetv) 타입 ASP : livetv[\d]?.{aspCode}.co.kr / mlivetv[\d]?.{aspCode}.co.kr */
const POPUP_ASP_HOSTNAME_PATTERN = /^(?:local\.|dev\.)?(?:livetv|mlivetv)\d*\.[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

export type ASPServiceType = 'iframe' | 'popup' | 'none';

/** iframe 타입 ASP 호스트 여부. wasp/masp/ wasp-new/masp-new + dev/local 변형. */
export const isIframeASPHostname = (rawHost: string): boolean => {
  return IFRAME_ASP_HOSTNAME_PATTERN.test(extractHostname(rawHost));
};

/** popup(livetv) 타입 ASP 호스트 여부. livetv / mlivetv 변형 + dev/local prefix. */
export const isPopupASPHostname = (rawHost: string): boolean => {
  return POPUP_ASP_HOSTNAME_PATTERN.test(extractHostname(rawHost));
};

/** ASP 호스트 여부 (iframe 또는 popup 어느 쪽이든). */
export const isASPHostname = (rawHost: string): boolean => {
  return isIframeASPHostname(rawHost) || isPopupASPHostname(rawHost);
};

/** 호스트의 ASP 서비스 유형을 단일 값으로 반환. */
export const classifyASPHostname = (rawHost: string): ASPServiceType => {
  const host = extractHostname(rawHost);
  if (IFRAME_ASP_HOSTNAME_PATTERN.test(host)) return 'iframe';
  if (POPUP_ASP_HOSTNAME_PATTERN.test(host)) return 'popup';
  return 'none';
};
